import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";

import { type RootState } from "../../../../Application/globaltypes/redux";
import * as reportingActions from "../../../state/packs/packActions";
import { selectPackLicensesData, selectPackLicensesState } from "../../../state/packs/packSlice";

import SpPackReportDrilldownList from "./SpPackReportDrilldownList";
import { RequestStatusRenderer } from "../../../../../components/requestStatsRenderer/RequestStatusRenderer";
import { usePerformanceBreadcrumbs } from "../../../../Library/Common/Hooks/usePerformanceBreadcrumbs";
import PackPurchasedPerformance from "../../../../Licensing/Packs/PurchasedView/Performance/PackPurchasedPerformance";

import "./spPackReportDrilldown.scss";
import { useAddOnQuery } from "features/Reporting/Content/queries/useAddOnQuery";
import { AddOnIds } from "enums";
import { type SpPackReportFilter } from "../SpPackReport";

export interface Props extends PropsFromRedux {
  packId: number;
  packName: string;
  filter: SpPackReportFilter;
  resetSelectedPack: () => void;
}

export const SpPackReportDrilldown: FC<Props> = ({
  packName,
  packId,
  packLicensesState,
  packLicensesData,
  filter,
  resetSelectedPack,
  actions,
  accountId,
}) => {
  // Selected customer logic for BSI customers only
  const [selectedCustomer, setSelectedCustomer] = useState<{ customerId: number; customerName: string }>();
  const { domElements, registerBreadcrumb } = usePerformanceBreadcrumbs();

  const { data: addons } = useAddOnQuery(accountId);
  const hasCreateAccount = addons?.find((addon) => addon.id === AddOnIds.CreateAccounts)?.isEnabled;

  useEffect(() => {
    actions.fetchPackLicenses(packId, filter);
  }, [actions, packId, filter]);

  const resetCustomerId = useCallback(() => setSelectedCustomer(undefined), []);

  useEffect(() => {
    if (selectedCustomer) {
      registerBreadcrumb({ text: selectedCustomer.customerName, action: resetCustomerId });
    }
  }, [selectedCustomer, resetCustomerId, registerBreadcrumb]);

  useEffect(() => {
    registerBreadcrumb({ text: packName, action: resetSelectedPack });
  }, [packName, resetSelectedPack, registerBreadcrumb]);

  const PackPurchasedPerformanceFilter = useMemo(
    () => ({ dateFrom: filter.dateFrom, dateTo: filter.dateTo }),
    [filter.dateFrom, filter.dateTo],
  );

  return (
    <div className="spPackReportDrilldownRoot">
      <div className="breadcrumbSpacing">{domElements}</div>
      {hasCreateAccount && selectedCustomer ? (
        <>
          <PackPurchasedPerformance
            filter={PackPurchasedPerformanceFilter}
            packId={packId}
            moboId={selectedCustomer.customerId}
          />
        </>
      ) : (
        <RequestStatusRenderer state={packLicensesState}>
          <SpPackReportDrilldownList
            reduxFinished
            packLicensesData={packLicensesData}
            setSelectedCustomer={setSelectedCustomer}
          />
        </RequestStatusRenderer>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  packLicensesState: selectPackLicensesState(state),
  packLicensesData: selectPackLicensesData(state),
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators(reportingActions, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SpPackReportDrilldown);
export default ConnectedComponent;
